import React, { useMemo } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";
import { formatValue2 } from "../../utils/Utils.js";

Font.register({
  family: "Satoshi",
  fonts: [
    { src: "https://cdn.saucey.com/fonts/Satoshi-Regular.woff" },
    {
      src: "https://cdn.saucey.com/fonts/Satoshi-Medium.woff",
      fontWeight: 500,
    },
    { src: "https://cdn.saucey.com/fonts/Satoshi-Bold.woff", fontWeight: 700 },
  ],
});

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 12, fontFamily: "Satoshi" },
  section: { marginBottom: 40 },
  headerRow: {
    flexDirection: "row",
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    fontSize: 12,
  },

  logo: { width: 177, height: 17, marginLeft: 1 }, // Adjust dimensions as needed
  header: { fontSize: 14, fontWeight: 600, marginBottom: 4 },
  headerDates: { flexDirection: "row", display: "flex" },
  storeName: { marginBottom: 5 },
  rightText: { fontSize: 12, textAlign: "right" },

  table: { display: "table", width: "auto" },
  tableRow: { flexDirection: "row", justifyContent: "space-between" },
  tableRowNotFullWidth: { flexDirection: "row" },
  tableCell: { margin: 5, fontSize: 12 },
  overviewCell0: { width: 55 },
  overviewCell1: { width: 40, textAlign: "right" },
  overviewCell2: { width: 40, textAlign: "right" },
  overviewCell3: { width: 75, textAlign: "right" },
  overviewCell4: { width: 115, textAlign: "right" },
  overviewCell4a: { width: 100, textAlign: "right" },
  overviewCell4b: { width: 100, textAlign: "right" },
  overviewCell5: { width: 60, textAlign: "right" },
  productCell0: { width: 350 },
  productCell1: { width: 40, textAlign: "right" },
  productCell2: { width: 60, textAlign: "right" },
  breakdownCell1: { width: 200 },
  breakdownCell2: { width: 60, textAlign: "right" },
});

const SalesReport = ({
  data,
  startDate,
  endDate,
  isAggregate,
  selectedLocations,
  selectedMerchant,
}) => {
  let date = useMemo(() => {
    if (startDate === endDate) {
      return moment(startDate).format("dddd, MMMM D, YYYY");
    } else {
      return `${moment(data.start_date_string || startDate).format("MMMM D, YYYY")} - ${moment(
        endDate
      ).format("MMMM D, YYYY")}`;
    }
  }, [startDate, endDate, data]);

  const storeName = useMemo(() => {
    if (isAggregate) {
      return selectedMerchant?.name;
    } else {
      return selectedLocations
        .map((location) => {
          return location.name;
        })
        .join(", ");
    }
  }, [selectedMerchant, selectedLocations, isAggregate]);

  if (!data) {
    return (
      <Document>
        <Page size="LETTER" style={styles.page}>
          <View style={styles.section}>
            <Text>Error generating report</Text>
          </View>
        </Page>
      </Document>
    );
  }
  return (
    <Document>
      <Page size="LETTER" style={styles.page} orientation="landscape">
        <View style={styles.section}>
          <View style={styles.headerRow}>
            <Image
              src="https://cdn.saucey.com/direct/web/direct-logo_darkbg.png"
              style={styles.logo}
            />
            <Text style={{ fontWeight: 500 }}>{date}</Text>
          </View>
          <View
            render={({ pageNumber, totalPages }) => {
              return (
                <View>
                  <View
                    style={{
                      // marginBottom: 16,
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    <Text>
                      {isAggregate && "Aggregated "}Sales Snapshot
                    </Text>
                  </View>
                  <Text style={{ marginBottom: 16 }}>
                    Page {pageNumber} of {totalPages}
                  </Text>
                </View>
              );
            }}
          ></View>

          <Text style={{ fontSize: 12, fontWeight: "bold" }}>{storeName}</Text>
          {/*{!isAggregate && <Text>{selectedLocation?.address}</Text>}*/}
          {/*{!isAggregate && (*/}
          {/*  <Text>*/}
          {/*    {selectedLocation?.city}, {selectedLocation?.state}{" "}*/}
          {/*    {selectedLocation?.zip}*/}
          {/*  </Text>*/}
          {/*)}*/}
          {isAggregate && <Text>(All Locations)</Text>}
        </View>
        <View style={styles.section}>
          <Text style={styles.header}>Sales Overview</Text>
          {/* Replace with dynamic table data */}
          <View style={styles.table}>
            <View
              style={{
                ...styles.tableRow,
                fontWeight: 600,
                borderBottom: 1,
                borderColor: "#000",
                marginBottom: 2,
                marginTop: 4,
              }}
            >
              <Text style={styles.overviewCell0}>Sale Type</Text>
              <Text style={styles.overviewCell1}>Orders</Text>
              <Text style={styles.overviewCell2}>Items</Text>
              <Text style={styles.overviewCell3}>Gross</Text>
              <Text style={styles.overviewCell4}>Application Fees</Text>
              <Text style={styles.overviewCell4}>Retailer Subtotal</Text>
              <Text style={styles.overviewCell4a}>Transaction Fee</Text>
              <Text style={styles.overviewCell5}>Net Payout</Text>
            </View>
            {[data.delivery, data.pickup].map((row, index) => (
              <View style={{ ...styles.tableRow, marginBottom: 2 }} key={index}>
                <Text style={styles.overviewCell0}>
                  {["Delivery", "Pickup"][index]}
                </Text>
                <Text style={styles.overviewCell1}>{row.order_count}</Text>
                <Text style={styles.overviewCell2}>{row.items}</Text>
                <Text style={styles.overviewCell3}>
                  ${formatValue2(row.sales)}
                </Text>
                <Text style={styles.overviewCell4}>
                  (${formatValue2(row.withholding)})
                </Text>
                <Text style={styles.overviewCell4}>
                  ${formatValue2(row.sales - row.withholding)}
                </Text>
                <Text style={styles.overviewCell4a}>
                  (${formatValue2(row.txn_fee)})
                </Text>
                <Text style={styles.overviewCell5}>
                  ${formatValue2(row.net_payout)}
                </Text>
              </View>
            ))}
            <View
              style={{
                ...styles.tableRow,
                fontWeight: 600,
                borderTop: 1,
                borderColor: "#000",
                paddingTop: 2,
              }}
            >
              <Text style={styles.overviewCell0}>Total</Text>
              <Text style={styles.overviewCell1}>
                {data.delivery.order_count + data.pickup.order_count}
              </Text>
              <Text style={styles.overviewCell2}>
                {data.delivery.items + data.pickup.items}
              </Text>
              <Text style={styles.overviewCell3}>{`$${formatValue2(
                data.delivery.sales + data.pickup.sales
              )}`}</Text>
              <Text style={styles.overviewCell4}>
                (
                {`$${formatValue2(
                  data.delivery.withholding + data.pickup.withholding
                )}`}
                )
              </Text>
              <Text style={styles.overviewCell4}>
                {`$${formatValue2(
                  data.delivery.sales +
                    data.pickup.sales -
                    data.delivery.withholding -
                    data.pickup.withholding
                )}`}
              </Text>
              <Text style={styles.overviewCell4a}>
                (
                {`$${formatValue2(
                  data.delivery.txn_fee + data.pickup.txn_fee
                )}`}
                )
              </Text>
              <Text style={styles.overviewCell5}>{`$${formatValue2(
                data.delivery.net_payout + data.pickup.net_payout
              )}`}</Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={styles.section}>
              <Text style={styles.header}>Customer Metrics</Text>
              <View style={styles.table}>
                <View
                  style={{ ...styles.tableRowNotFullWidth, marginBottom: 2 }}
                >
                  <Text style={styles.breakdownCell1}>Average Order Value</Text>
                  <Text style={styles.breakdownCell2}>
                    ${formatValue2(data.customer_metrics.aov)}
                  </Text>
                </View>
                <View
                  style={{ ...styles.tableRowNotFullWidth, marginBottom: 2 }}
                >
                  <Text style={styles.breakdownCell1}>New Customers</Text>
                  <Text style={styles.breakdownCell2}>
                    {data.customer_metrics.new_customer_count}
                  </Text>
                </View>
                <View
                  style={{ ...styles.tableRowNotFullWidth, marginBottom: 2 }}
                >
                  <Text style={styles.breakdownCell1}>Returning Customers</Text>
                  <Text style={styles.breakdownCell2}>
                    {data.customer_metrics.returning_customer_count}
                  </Text>
                </View>
              </View>
            </View>
            <View style={styles.section}>
              <Text style={styles.header}>Fee Breakdown</Text>
              <View style={styles.table}>
                <View
                  style={{ ...styles.tableRowNotFullWidth, marginBottom: 2 }}
                >
                  <Text style={styles.breakdownCell1}>
                    Payment Gateway Transaction Fee
                  </Text>
                  <Text style={styles.breakdownCell2}>
                    ${formatValue2(data.fees.gateway_transaction_fee)}
                  </Text>
                </View>
                <View
                  style={{ ...styles.tableRowNotFullWidth, marginBottom: 2 }}
                >
                  <Text style={styles.breakdownCell1}>Compliance Fee</Text>
                  <Text style={styles.breakdownCell2}>
                    ${formatValue2(data.fees.compliance_fee)}
                  </Text>
                </View>
                <View
                  style={{ ...styles.tableRowNotFullWidth, marginBottom: 2 }}
                >
                  <Text style={styles.breakdownCell1}>
                    Delivery Fees (Paid to DSP)
                  </Text>
                  <Text style={styles.breakdownCell2}>
                    ${formatValue2(data.fees.delivery_fee)}
                  </Text>
                </View>
                <View
                  style={{ ...styles.tableRowNotFullWidth, marginBottom: 2 }}
                >
                  <Text style={styles.breakdownCell1}>Tips (Paid to DSP)</Text>
                  <Text style={styles.breakdownCell2}>
                    ${formatValue2(data.fees.delivery_tips || 0)}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>
      <Page size="LETTER" style={styles.page} orientation="landscape">
        <View style={styles.section}>
          <View style={styles.headerRow}>
            <Image
              src="https://cdn.saucey.com/direct/web/direct-logo_darkbg.png"
              style={styles.logo}
            />
            <Text style={{ fontWeight: 500 }}>{date}</Text>
          </View>
          <View
            render={({ pageNumber, totalPages }) => {
              return (
                <View>
                  <View
                    style={{
                      // marginBottom: 16,
                      fontSize: 16,
                      fontWeight: 700,
                    }}
                  >
                    <Text>
                      {isAggregate && "Aggregated "}Sales Snapshot
                    </Text>
                  </View>
                  <Text style={{ marginBottom: 16 }}>
                    Page {pageNumber} of {totalPages}
                  </Text>
                </View>
              );
            }}
          ></View>
        </View>

        <View style={styles.section}>
          <Text style={styles.header}>Top Selling Items</Text>
          {/* Replace with dynamic table data */}
          <View style={styles.table}>
            <View
              style={{
                ...styles.tableRow,
                fontWeight: 600,
                borderBottom: 1,
                borderColor: "#000",
                marginBottom: 2,
                marginTop: 4,
              }}
            >
              <Text style={styles.productCell0}>Product</Text>
              <Text style={styles.productCell1}>Units</Text>
              <Text style={styles.productCell2}>Sales</Text>
            </View>
            {!data.items.length && (
              <View style={{ ...styles.tableRow, marginBottom: 2 }}>
                <Text style={styles.productCell0}>No items sold</Text>
              </View>
            )}
            {data.items.map((item, index) => {
              return (
                <View
                  style={{ ...styles.tableRow, marginBottom: 2 }}
                  key={index}
                >
                  <Text style={styles.productCell0}>
                    {`${item.title}${item.subtitle ? ` ${item.subtitle}` : ""}${
                      item.volume ? ` - ${item.volume}` : ""
                    }`}
                  </Text>
                  <Text style={styles.productCell1}>{item.quantity}</Text>
                  <Text style={styles.productCell2}>
                    ${formatValue2(item.revenue)}
                  </Text>
                </View>
              );
            })}
            <View
              style={{
                ...styles.tableRow,
                fontWeight: 600,
                borderTop: 1,
                borderColor: "#000",
                paddingTop: 2,
              }}
            ></View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default SalesReport;
